import React, { useState, useEffect } from "react";
import {
  Card,
  Input,
  Headline,
  InputLabel,
  Flex,
  Paragraph,
  Button,
} from "_styleguide";
import Footer from "../_components/Footer/Footer";
import { useLead } from "_contexts/useLead";
import { useSettings } from "_contexts/useSettings";
import { isDarkColor } from "_helpers/colors";
import "./CustomPage.scss";

function CustomPage(props) {
  const { lead, updateLead } = useLead();
  const { settings } = useSettings();
  const customPageData = props.settings?.customPageData;
  const primaryColor = settings?.primary_color || "#0f766e";
  const primaryFontColor = isDarkColor(primaryColor) ? "#fff" : "#000";

  // Local state for custom field value
  const [fieldValue, setFieldValue] = useState("");

  // Load existing value from lead.customFields if it exists
  useEffect(() => {
    if (customPageData?.input_type === "custom_field" && customPageData?.custom_field_name) {
      const existingValue = lead?.customFields?.[customPageData.custom_field_name] || "";
      setFieldValue(existingValue);
    }
  }, [lead?.customFields, customPageData?.custom_field_name, customPageData?.input_type]);

  // Handle custom field value change
  const handleFieldChange = (value) => {
    setFieldValue(value);
    // Store in lead.customFields object using the field NAME as the key
    const updatedCustomFields = {
      ...(lead?.customFields || {}),
      [customPageData.custom_field_name]: value,
    };
    updateLead("customFields", updatedCustomFields);
  };

  // Handle CTA button click
  const handleCTAClick = () => {
    if (!customPageData) return;

    const { cta_type, cta_value } = customPageData;

    switch (cta_type) {
      case "phone":
        window.location.href = `tel:${cta_value}`;
        break;
      case "email":
        window.location.href = `mailto:${cta_value}`;
        break;
      case "url":
        window.open(cta_value, "_blank", "noopener,noreferrer");
        break;
      default:
        break;
    }
  };

  // Validation for custom field
  const isValid = () => {
    if (customPageData?.input_type === "custom_field") {
      if (customPageData?.is_required && !fieldValue.trim()) {
        return false;
      }
    }
    return true;
  };

  if (!customPageData) {
    return (
      <Card id="custom-page">
        <div className="apply-form">
          <Headline>Page Not Found</Headline>
          <Paragraph>This page could not be loaded.</Paragraph>
        </div>
      </Card>
    );
  }

  return (
    <Card id="custom-page">
      <div className="apply-form">
        <Headline>{customPageData.title}</Headline>
        {customPageData.subtitle ? (
          <Paragraph
            opacity={0.6}
            align="left"
            size="14px"
            dangerouslySetInnerHTML={{
              __html: customPageData.subtitle,
            }}
          />
        ) : null}

        {/* Custom Field Input */}
        {customPageData.input_type === "custom_field" && (
          <Flex vertical style={{ marginTop: customPageData.subtitle ? "0" : "24px" }}>
            {customPageData.input_label && (
              <InputLabel htmlFor="custom-field">
                {customPageData.input_label}
              </InputLabel>
            )}
            <Input
              id="custom-field"
              value={fieldValue}
              onChange={(e) => handleFieldChange(e.target.value)}
              size="medium"
              placeholder={customPageData.input_placeholder || "Enter your response..."}
              autoFocus
            />
          </Flex>
        )}

        {/* CTA Button */}
        {customPageData.input_type === "cta" && (
          <Flex vertical style={{ marginTop: "24px" }}>
            <Button
              block
              size="large"
              style={{ background: primaryColor, color: primaryFontColor }}
              onClick={handleCTAClick}
            >
              {customPageData.cta_button_text || "Click Here"}
            </Button>
          </Flex>
        )}
      </div>
      {/* Only show Next button if there's a next page */}
      {props.settings?.next && (
        <Footer
          next={props.settings?.next}
          settings={props.settings}
          disableNext={!isValid()}
        />
      )}
    </Card>
  );
}

export default CustomPage;
